import { Button } from "@interstate/components/Button";
import { useToaster } from "@interstate/components/Toast";
import { useContext, useMemo, useState } from "react";
import { Spinner } from "@interstate/components/Spinner";
import {
  ConversationContext,
  ConversationDetailsContext,
  usePhoneReply
} from "../../context";
import {
  ConversationDirection,
  ConversationTypeEnum,
  ConversationViewTypeEnum
} from "@coxauto-ui/cxm/communications/interfaces";
import { replyButtonTestId } from "../../testIds";
import {
  SendLogCallDetailsRequest,
  getConversationItemFromSendAction,
  isResponseError,
  sendLogCall
} from "@coxauto-ui/cxm/communications/api";
import { TagManager } from "@coxauto-ui/core-third-party-ga4";
import {
  ConditionalToolTipWrapper,
  notifyCommunicationSent
} from "@coxauto-ui/cxm/communications/util";
import { ButtonContainer, removeVideoUrls } from "./message-send-button-shared";

export interface PhoneMessageSendButtonProps {
  onMessageSendFailed: () => void;
  onMessageSendFailedSetMsg: (msg: string) => void;
}

/**
 * Phone-specific message send button component.
 * Uses phone reply context to prevent cross-channel re-renders.
 */
export function PhoneMessageSendButton(props: PhoneMessageSendButtonProps) {
  const { toast: toaster } = useToaster();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const {
    autoLeadId,
    repairOrderId,
    customerId,
    customerSystem,
    defaultTab,
    env,
    getUserName,
    hasActiveTask,
    setDefaultMessageContent,
    taskId,
    serviceAppointmentId,
    completeTask,
    inboxMessageHandler,
    isIframeMode
  } = useContext(ConversationContext);

  const { setConversationItems, selectedConversationItem, toggleSetShowReply } =
    useContext(ConversationDetailsContext);

  const {
    replyText,
    setReplyText,
    messageCallContacted,
    setMessageCallContacted,
    messageCallType,
    setMessageCallType,
    selectedToChannelValue,
    setSelectedToChannelValue
  } = usePhoneReply();

  const [loading, setLoading] = useState(false);

  const closeOnSend = () => toggleSetShowReply && toggleSetShowReply();

  const clearPhoneReplyFieldsAndDefaults = () => {
    // Clear phone reply fields directly
    setReplyText("");
    setMessageCallContacted("");
    setMessageCallType("" as any);
    setSelectedToChannelValue("");

    // Clear the default message content
    setDefaultMessageContent(null);
  };

  const applyDisabled = useMemo(() => {
    return loading || !(replyText?.trim().length > 0);
  }, [loading, replyText]);

  const sendReply = async () => {
    if (replyText.trim().length === 0) {
      return;
    }

    setLoading(true);

    const channelValue = selectedToChannelValue || "";
    const sendLogCallRequest: SendLogCallDetailsRequest = {
      autoLeadId,
      repairOrderId,
      consumerId: customerId,
      consumerRecordSystem: customerSystem,
      callType: messageCallType || "",
      contacted: messageCallContacted || "",
      notes: replyText,
      taskId: hasActiveTask ? taskId : undefined,
      customerNumber: channelValue.replace(/\D/g, ""),
      serviceAppointmentId
    };
    const response = await sendLogCall(sendLogCallRequest, env);

    if (response === true) {
      TagManager.event({
        event: "productEvent",
        eventName: "Call Logged",
        eventProperties: {
          packagedBusinessCapability: "OCC Inbox",
          commonCustomerId: customerId,
          communicationChannel: ConversationTypeEnum.PHONE,
          replyContext:
            selectedConversationItem != null
              ? ConversationViewTypeEnum.DETAIL
              : ConversationViewTypeEnum.LIST,
          errorMessage: undefined
        }
      });

      // Notify parent window if in iframe mode
      if (isIframeMode && inboxMessageHandler) {
        notifyCommunicationSent(
          inboxMessageHandler,
          "CALL",
          ConversationTypeEnum.PHONE,
          true,
          hasActiveTask
        );
      }

      const conversationItem = getConversationItemFromSendAction(
        getUserName(),
        (messageCallType || "").toString() as ConversationDirection,
        ConversationTypeEnum.PHONE,
        channelValue,
        new Date(),
        removeVideoUrls(replyText.substring(0, 300)),
        "",
        undefined,
        [] // Phone doesn't have attachments
      );

      setConversationItems(current => [...current, conversationItem]);
      clearPhoneReplyFieldsAndDefaults();

      // Complete the task if this was sent on the defaultTab channel
      if (hasActiveTask && defaultTab === ConversationTypeEnum.PHONE) {
        completeTask();
      }

      closeOnSend();
    } else if (isResponseError(response)) {
      const error = "Failed to send message.";

      TagManager.event({
        event: "productEvent",
        eventName: "Error Displayed",
        eventProperties: {
          packagedBusinessCapability: "OCC Inbox",
          commonCustomerId: customerId,
          communicationChannel: ConversationTypeEnum.PHONE,
          replyContext: undefined,
          errorMessage: error
        }
      });

      if (response?.statusCode === 403) {
        props.onMessageSendFailedSetMsg("Compliance Issue");
        props.onMessageSendFailed();
      }

      // Notify parent window if in iframe mode
      if (isIframeMode && inboxMessageHandler) {
        notifyCommunicationSent(
          inboxMessageHandler,
          "CALL",
          ConversationTypeEnum.PHONE,
          false,
          hasActiveTask
        );
      }

      // Clear phone channel fields on error
      clearPhoneReplyFieldsAndDefaults();
      toaster("Error", error, noop, undefined);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <ButtonContainer>
      <ConditionalToolTipWrapper condition={false} toolTipContent="">
        <Button
          data-testid={replyButtonTestId}
          disabled={applyDisabled}
          id="conversation-reply-button"
          onClick={() => sendReply()}
        >
          Save
          {loading && (
            <Spinner
              id="conversation-reply-button-loading-indicator"
              size={30}
            />
          )}
        </Button>
      </ConditionalToolTipWrapper>
    </ButtonContainer>
  );
}

export default PhoneMessageSendButton;
